import styled, { css, keyframes } from 'styled-components'
import { Link } from 'react-router-dom'

/*
 * Menu no estilo "app de produtividade" (referência: ElevenLabs): fundo branco, itens com
 * ícone + texto em cinza-escuro, destaque da tela atual em cinza claro e só um toque de
 * azul no ícone. A cor da marca fica na barra do topo.
 */
export const navColor = {
  bg: '#ffffff',
  line: '#ececf0',
  text: '#18181b',
  soft: '#3f3f46',
  muted: '#71717a',
  faint: '#a1a1aa',
  hover: '#f4f4f5',
  active: '#ededf0',
  activeText: '#09090b',
  accent: '#1f63e3',
  scrim: 'rgba(9, 9, 11, 0.45)',
}

export const EXPANDED_WIDTH = 252
export const RAIL_WIDTH = 64

const font = "'Plus Jakarta Sans', 'Poppins', 'Source Sans Pro', sans-serif"

const ellipsis = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(-6px); }
  to { opacity: 1; transform: translateX(0); }
`

const thinScroll = css`
  scrollbar-width: thin;
  scrollbar-color: #e4e4e7 transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e4e4e7;
    border-radius: 8px;
    border: 2px solid ${navColor.bg};
  }
`

/* ---------- área do app: menu lateral + (barra do topo e conteúdo) ---------- */

export const Shell = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-width: 0;

  & > .ps-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }

  & > .ps-main > .section-container {
    flex: 1;
    min-width: 0;
  }
`

/* ---------- barra lateral do computador ---------- */

export const Sidebar = styled.nav`
  position: sticky;
  top: 0;
  flex: none;
  z-index: 900;
  width: ${({ collapsed }) => (collapsed ? RAIL_WIDTH : EXPANDED_WIDTH)}px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${navColor.bg};
  border-right: 1px solid ${navColor.line};
  font-family: ${font};
  transition: width 0.22s ease;
  overflow: hidden;
`

/** Marca no topo da barra lateral (mesma altura da barra colorida). */
export const Brand = styled(Link)`
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 56px;
  padding: 0 ${({ collapsed }) => (collapsed ? 0 : 18)}px;
  justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'flex-start')};
  text-decoration: none !important;
  color: ${navColor.text} !important;

  .mark {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    background: ${({ staging }) =>
      staging
        ? 'linear-gradient(135deg, #673ab7 0%, #c00f4b 100%)'
        : 'linear-gradient(90deg, #096dd9 0%, #1f63e3 55%, #1770c1 100%)'};

    img {
      width: 15px;
      height: 17px;
    }
  }

  .word {
    font-size: 17px;
    font-weight: 800;
    letter-spacing: -0.02em;
    ${ellipsis}
  }
`

/* ---------- gaveta (tablet e celular) ---------- */

export const Scrim = styled.div`
  position: fixed;
  inset: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10000;
  background: ${navColor.scrim};
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`

export const Drawer = styled.nav`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 10001;
  width: ${({ full }) => (full ? '100vw' : '320px')};
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  background: ${navColor.bg};
  font-family: ${font};
  box-shadow: 8px 0 32px rgba(9, 9, 11, 0.16);
  transform: translateX(${({ open }) => (open ? '0' : '-105%')});
  transition: transform 0.24s cubic-bezier(0.2, 0.8, 0.2, 1);
  padding-bottom: env(safe-area-inset-bottom, 0px);
`

export const DrawerHead = styled.div`
  flex: none;
  --mark: ${({ staging }) =>
    staging
      ? 'linear-gradient(135deg, #673ab7 0%, #c00f4b 100%)'
      : 'linear-gradient(90deg, #096dd9 0%, #1f63e3 55%, #1770c1 100%)'};
  display: flex;
  align-items: center;
  gap: 10px;
  height: 60px;
  padding: 0 10px 0 18px;

  .mark {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    background: var(--mark);

    img {
      width: 15px;
      height: 17px;
    }
  }

  b {
    flex: 1;
    font-size: 17px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: ${navColor.text};
  }
`

export const IconButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ size }) => size || 36}px;
  height: ${({ size }) => size || 36}px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${navColor.muted};
  font-size: 15px;
  cursor: pointer;

  &:hover {
    background: ${navColor.hover};
    color: ${navColor.text};
  }

  &:focus-visible {
    outline: 2px solid ${navColor.accent};
    outline-offset: 1px;
  }
`

/* ---------- busca (gaveta) ---------- */

export const Search = styled.label`
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 12px 8px;
  padding: 0 10px;
  height: ${({ touch }) => (touch ? 46 : 38)}px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e4e4e7;
  color: ${navColor.muted};
  cursor: text;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: #a1a1aa;
    box-shadow: 0 0 0 3px rgba(161, 161, 170, 0.18);
  }

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font: inherit;
    font-size: ${({ touch }) => (touch ? 16 : 14)}px;
    color: ${navColor.text};

    &::placeholder {
      color: ${navColor.faint};
    }
  }

  kbd {
    flex: none;
    padding: 1px 6px;
    border-radius: 6px;
    border: 1px solid #e4e4e7;
    background: #fafafa;
    font-family: inherit;
    font-size: 11px;
    color: ${navColor.muted};
  }
`

/* ---------- lista ---------- */

export const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 10px 12px;
  overscroll-behavior: contain;
  ${thinScroll}
`

const rowBase = css`
  position: relative;
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  min-height: ${({ touch }) => (touch ? 46 : 36)}px;
  padding: 0 10px;
  margin: 1px 0;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: ${navColor.soft};
  font-family: ${font};
  font-size: ${({ touch }) => (touch ? 15.5 : 14.5)}px;
  font-weight: 500;
  letter-spacing: -0.005em;
  text-align: left;
  text-decoration: none !important;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: ${navColor.hover};
    color: ${navColor.text};
  }

  &:focus-visible {
    outline: 2px solid ${navColor.accent};
    outline-offset: -2px;
  }

  .nav-icon {
    flex: none;
    width: 20px;
    text-align: center;
    font-size: 15px;
    color: ${navColor.muted};
    transition: color 0.12s ease;
  }

  &:hover .nav-icon {
    color: ${navColor.soft};
  }

  .label {
    flex: 1;
    min-width: 0;
    ${ellipsis}
  }

  .chevron {
    flex: none;
    font-size: 10px;
    color: ${navColor.faint};
    transition: transform 0.2s ease;
  }

  ${({ active }) =>
    active &&
    css`
      background: ${navColor.active} !important;
      color: ${navColor.activeText} !important;
      font-weight: 600;

      .nav-icon {
        color: ${navColor.accent} !important;
      }
    `}

  ${({ open }) =>
    open &&
    css`
      .chevron {
        transform: rotate(90deg);
      }
    `}

  ${({ current }) =>
    current &&
    css`
      color: ${navColor.text};

      .nav-icon {
        color: ${navColor.accent};
      }
    `}
`

export const RowLink = styled(Link)`
  ${rowBase}
`

export const RowButton = styled.button`
  ${rowBase}
`

export const SubList = styled.div`
  position: relative;
  margin: 1px 0 6px 21px;
  padding-left: 10px;
  border-left: 1px solid ${navColor.line};
  animation: ${fadeIn} 0.16s ease;
`

const subRow = css`
  min-height: ${({ touch }) => (touch ? 42 : 32)}px;
  font-size: ${({ touch }) => (touch ? 15 : 13.5)}px;
  font-weight: 500;
  gap: 9px;
  padding: 0 8px;
  border-radius: 8px;

  .nav-icon {
    width: 16px;
    font-size: 12.5px;
  }
`

export const SubLink = styled(RowLink)`
  ${subRow}
`

export const SubButton = styled(RowButton)`
  ${subRow}
`

export const AreaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  & > button {
    flex: 1;
    min-width: 0;
  }

  & > a {
    opacity: 0;
  }

  &:hover > a,
  & > a:focus-visible,
  & > a[data-active='1'] {
    opacity: 1;
  }

  .count {
    flex: none;
    min-width: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: ${navColor.hover};
    color: ${navColor.muted};
    font-size: 11px;
    font-weight: 600;
    text-align: center;
    line-height: 18px;
  }
`

export const StationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px 4px;
  margin: 1px 0 6px 4px;

  a {
    min-height: ${({ touch }) => (touch ? 40 : 30)}px;
    font-size: ${({ touch }) => (touch ? 14.5 : 13)}px;
    font-weight: 500;
    padding: 0 8px;
  }
`

export const Badge = styled.span`
  flex: none;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 0.01em;
  color: ${({ kind }) =>
    kind === 'updated' ? '#1f63e3' : kind === 'beta' ? '#6d28d9' : '#b45309'};
  background: ${({ kind }) =>
    kind === 'updated' ? '#e8f0ff' : kind === 'beta' ? '#f1e9ff' : '#fef3c7'};
`

export const Group = styled.div`
  & + & {
    margin-top: 1px;
  }
`

export const Divider = styled.div`
  height: 1px;
  margin: 8px 6px;
  background: ${navColor.line};
`

/** Título de grupo, como o "Pinned" da referência. */
export const Caption = styled.div`
  padding: 14px 10px 6px;
  font-size: 12.5px;
  font-weight: 500;
  color: ${navColor.faint};
`

export const Empty = styled.div`
  padding: 18px 12px;
  color: ${navColor.muted};
  font-size: 13.5px;
  text-align: center;

  b {
    display: block;
    color: ${navColor.text};
    margin-bottom: 2px;
  }
`

export const ResultGroup = styled.div`
  padding: 10px 12px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${navColor.faint};
`

export const ResultPath = styled.span`
  display: block;
  font-size: 11.5px;
  font-weight: 500;
  color: ${navColor.faint};
  ${ellipsis}
`

export const SortButtons = styled.span`
  flex: none;
  display: inline-flex;
  gap: 2px;

  button {
    width: 26px;
    height: 26px;
    border: 1px solid ${navColor.line};
    border-radius: 7px;
    background: #ffffff;
    color: ${navColor.muted};
    font-size: 11px;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: #a1a1aa;
      color: ${navColor.text};
    }

    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
  }
`

/** Rodapé: "Organizar menu" e "Recolher menu" no mesmo formato dos itens. */
export const Foot = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 8px 10px 10px;
  border-top: 1px solid ${navColor.line};
  ${({ hidden }) => hidden && 'display: none;'}

  & > button {
    display: flex;
    align-items: center;
    justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'flex-start')};
    gap: 11px;
    width: 100%;
    height: 36px;
    padding: 0 10px;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: ${navColor.muted};
    font-family: ${font};
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    ${ellipsis}

    i {
      flex: none;
      width: 20px;
      text-align: center;
      font-size: 14px;
    }

    &:hover {
      background: ${navColor.hover};
      color: ${navColor.text};
    }

    &[data-on='true'] {
      background: ${navColor.active};
      color: ${navColor.text};
      font-weight: 600;
    }
  }
`

/* ---------- trilho de ícones (barra recolhida) ---------- */

export const Rail = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 0 10px;

  &::-webkit-scrollbar {
    width: 0;
  }
`

const railItem = css`
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: ${navColor.muted};
  text-decoration: none !important;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  i {
    font-size: 16px;
  }

  &:hover {
    background: ${navColor.hover};
    color: ${navColor.text};
  }

  &:focus-visible {
    outline: 2px solid ${navColor.accent};
    outline-offset: 1px;
  }

  ${({ active }) =>
    active &&
    css`
      background: ${navColor.active} !important;
      color: ${navColor.accent} !important;
    `}

  ${({ open }) =>
    open &&
    css`
      background: ${navColor.hover};
      color: ${navColor.text};
    `}
`

export const RailLink = styled(Link)`
  ${railItem}
`

export const RailButton = styled.button`
  ${railItem}
`

/** Dica com o nome ao passar o mouse no trilho (fica fora do trilho, que corta o conteúdo). */
export const RailTip = styled.div`
  position: fixed;
  z-index: 1100;
  left: ${RAIL_WIDTH + 6}px;
  padding: 5px 9px;
  border-radius: 7px;
  background: #18181b;
  color: #ffffff;
  font-family: ${font};
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  transform: translateY(-50%);
  box-shadow: 0 4px 14px rgba(9, 9, 11, 0.18);
`

export const Flyout = styled.div`
  position: fixed;
  z-index: 1000;
  left: ${RAIL_WIDTH + 6}px;
  width: 290px;
  max-height: calc(100vh - 16px);
  display: flex;
  flex-direction: column;
  background: ${navColor.bg};
  border: 1px solid ${navColor.line};
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(9, 9, 11, 0.14), 0 2px 6px rgba(9, 9, 11, 0.06);
  font-family: ${font};
  animation: ${fadeIn} 0.14s ease;

  .title {
    flex: none;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px 6px;
    font-size: 14.5px;
    font-weight: 700;
    color: ${navColor.text};

    i {
      color: ${navColor.accent};
    }
  }
`

/* ---------- barra colorida do topo: caminho e busca ---------- */

export const Trail = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-family: ${font};
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.72);

  .sep {
    flex: none;
    font-size: 10px;
    opacity: 0.6;
  }

  .crumb {
    ${ellipsis}
    max-width: 220px;
  }

  .crumb:last-child {
    color: #ffffff;
    font-weight: 700;
  }

  /* com pouco espaço fica só a tela atual */
  @media (max-width: 1439px) {
    .crumb:not(:last-child),
    .sep {
      display: none;
    }
  }
`

export const TopSearch = styled.div`
  position: relative;
  width: 100%;
  max-width: 440px;
  font-family: ${font};

  label {
    display: flex;
    align-items: center;
    gap: 9px;
    height: 36px;
    padding: 0 10px 0 12px;
    margin: 0;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.22);
    color: rgba(255, 255, 255, 0.85);
    cursor: text;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    &:focus-within {
      background: #ffffff;
      border-color: #ffffff;
      color: ${navColor.muted};
      box-shadow: 0 6px 18px rgba(9, 9, 11, 0.18);
    }
  }

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    color: inherit;

    &::placeholder {
      color: rgba(255, 255, 255, 0.78);
    }
  }

  label:focus-within input {
    color: ${navColor.text};

    &::placeholder {
      color: ${navColor.faint};
    }
  }

  kbd {
    flex: none;
    padding: 1px 6px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
    font-family: inherit;
    font-size: 11px;
    color: inherit;
  }

  label:focus-within kbd {
    border-color: #e4e4e7;
    background: #fafafa;
  }
`

export const TopResults = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  min-width: 340px;
  max-height: min(440px, calc(100vh - 90px));
  overflow-y: auto;
  padding: 6px;
  z-index: 1200;
  background: #ffffff;
  border: 1px solid ${navColor.line};
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(9, 9, 11, 0.16), 0 2px 6px rgba(9, 9, 11, 0.06);
  ${thinScroll}

  .hint {
    padding: 8px 10px 6px;
    font-size: 12px;
    font-weight: 500;
    color: ${navColor.faint};
  }
`
